import React, { useState } from "react";

/**
 * Smart media renderer for storefront product pages.
 *
 * Priority order:
 *   1. `videoUrl` (merchant-supplied override on the campaign)
 *   2. `productVideoUrl` (auto-pulled from Shopify product media)
 *   3. `imageUrl` (image fallback)
 *
 * Supports:
 *   - YouTube (youtube.com / youtu.be / m.youtube.com)
 *   - Vimeo (vimeo.com / player.vimeo.com)
 *   - Direct MP4 / WebM (and Shopify-hosted CDN videos)
 *   - HLS streams (.m3u8) via native <video>
 */

function detectVideoKind(url) {
  if (!url) return null;
  const u = url.toLowerCase();
  if (u.includes("youtube.com") || u.includes("youtu.be")) return "youtube";
  if (u.includes("vimeo.com")) return "vimeo";
  if (/\.(mp4|webm|mov|m4v|m3u8)(\?|$)/.test(u)) return "native";
  if (u.includes("cdn.shopify.com")) return "native";
  // Default: try native; browser will reject if unsupported
  return "native";
}

function youtubeEmbed(url) {
  // Handles: youtu.be/<id>, youtube.com/watch?v=<id>, youtube.com/embed/<id>, youtube.com/shorts/<id>
  try {
    const m =
      url.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/) ||
      url.match(/[?&]v=([A-Za-z0-9_-]{6,})/) ||
      url.match(/embed\/([A-Za-z0-9_-]{6,})/) ||
      url.match(/shorts\/([A-Za-z0-9_-]{6,})/);
    const id = m ? m[1] : null;
    if (!id) return url;
    // Loop + mute + autoplay so it behaves like a hero
    return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=1&modestbranding=1&rel=0&playsinline=1`;
  } catch {
    return url;
  }
}

function vimeoEmbed(url) {
  try {
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/) || url.match(/player\.vimeo\.com\/video\/(\d+)/);
    const id = m ? m[1] : null;
    if (!id) return url;
    return `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&loop=1&background=0&playsinline=1`;
  } catch {
    return url;
  }
}

export default function ProductMedia({
  imageUrl,
  productVideoUrl,
  videoUrl,
  title,
  className = "",
  testId = "product-media",
}) {
  const finalVideo = videoUrl || productVideoUrl;
  const kind = detectVideoKind(finalVideo);
  const [nativeFailed, setNativeFailed] = useState(false);

  // Image-only fallback
  if (!finalVideo || (kind === "native" && nativeFailed)) {
    return (
      <div
        className={`aspect-square overflow-hidden rounded-2xl bg-gray-100 ${className}`}
        data-testid={`${testId}-image`}
      >
        {imageUrl ? (
          <img src={imageUrl} alt={title || ""} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 text-sm">No media</div>
        )}
      </div>
    );
  }

  if (kind === "youtube") {
    return (
      <div
        className={`aspect-square overflow-hidden rounded-2xl bg-black ${className}`}
        data-testid={`${testId}-youtube`}
      >
        <iframe
          src={youtubeEmbed(finalVideo)}
          title={title || "Product video"}
          className="w-full h-full"
          frameBorder="0"
          allow="autoplay; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }

  if (kind === "vimeo") {
    return (
      <div
        className={`aspect-square overflow-hidden rounded-2xl bg-black ${className}`}
        data-testid={`${testId}-vimeo`}
      >
        <iframe
          src={vimeoEmbed(finalVideo)}
          title={title || "Product video"}
          className="w-full h-full"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  // Native <video> for MP4 / WebM / HLS / Shopify CDN
  return (
    <div
      className={`aspect-square overflow-hidden rounded-2xl bg-black ${className}`}
      data-testid={`${testId}-native`}
    >
      <video
        className="w-full h-full object-cover"
        src={finalVideo}
        poster={imageUrl || undefined}
        autoPlay
        muted
        loop
        playsInline
        controls
        onError={() => setNativeFailed(true)}
      />
    </div>
  );
}
